// IE Hub - Text Field Component
// Enhanced text input with validation and JSON schema integration

import React, { forwardRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Typography,
  alpha,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import ConditionalContainer from '../layout/ConditionalContainer';

interface ValidationRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  patternFlags?: string;
}

interface TextFieldProps extends Omit<MuiTextFieldProps, 'variant' | 'placeholder' | 'label'> {
  type?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  autoComplete?: string;
  placeholder?: string;
  showPasswordToggle?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  showCharacterCount?: boolean;
  showValidationIcon?: boolean;
  isLoading?: boolean;
  // When true, suppress local (onChange/onBlur) validation and only show parent errors
  deferValidation?: boolean;
  /**
   * Styles applied to the underlying MUI TextField component.
   * Note: the existing `sx` prop styles the surrounding container (legacy behaviour).
   */
  textFieldSx?: MuiTextFieldProps['sx'];
  // Custom UI blocks
  helperTop?: string | undefined;        // Above input
  helperAttached?: string | undefined;   // Attached block inside container
  helperBottom?: string | undefined;     // Below input
  showErrorBelow?: boolean;  // controls whether error renders beneath input
  label?: string | undefined;            // Rendered above the field (not inside input)
  // ConditionalContainer integration
  title?: string;
  description?: string;
  containerVariant?: 'paper' | 'outlined' | 'plain';
  showErrorInContainer?: boolean;

  // Mobile keyboard and input behavior (defaults to normal text keyboard)
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoCapitalize?: 'off' | 'none' | 'sentences' | 'words' | 'characters';
  autoCorrect?: 'on' | 'off';
  spellCheck?: boolean;

  // LogRocket privacy masking - when true, adds data-private attribute to mask PII in recordings
  // Defaults to true for security; set to false for non-PII fields
  dataPrivate?: boolean;
}

// Styled TextField with enhanced visual feedback
const Wrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const AttachedBlock = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
  border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem'
}));

const StyledTextField = styled(MuiTextField, {
  shouldForwardProp: (prop) => prop !== 'error',
})<{ error?: boolean }>(({ theme, error }) => {
  return {
    '& .MuiOutlinedInput-root': {
      // Single-line fields keep a consistent height.
      minHeight: '44px',
      height: '44px',
      alignItems: 'center',
      transition: 'all 0.2s ease-in-out',
      backgroundColor: theme.palette.background.paper,

      // Default border styling
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
        borderWidth: '1px',
      },

      // Error state styling - highest priority
      ...(error && {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: '2px !important',
        },
      }),

      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: error
            ? `${theme.palette.error.main} !important`
            : theme.palette.primary.main,
          borderWidth: '2px !important',
        },
      },

      '&.Mui-focused': {
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 0 2px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.2)}`,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: error
            ? `${theme.palette.error.main} !important`
            : `${theme.palette.primary.main} !important`,
          borderWidth: '2px !important',
        },
      },

      // MUI error class - ensure it works
      '&.Mui-error': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: '2px !important',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: '2px !important',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: `${theme.palette.error.main} !important`,
          borderWidth: '2px !important',
        },
      },

      '&.Mui-disabled': {
        backgroundColor: alpha(theme.palette.action.disabled, 0.05),
      },
    },

    // Multiline fields must be allowed to grow (e.g., minRows) and align text to the top.
    '& .MuiOutlinedInput-root.MuiInputBase-multiline, & .MuiOutlinedInput-root.MuiOutlinedInput-multiline': {
      height: 'auto',
      alignItems: 'flex-start',
    },

    '& .MuiInputLabel-outlined': {
      '&.Mui-focused': {
        color: error ? theme.palette.error.main : theme.palette.primary.main,
      },
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginTop: theme.spacing(0.75),
      fontSize: '0.8125rem',
      transition: 'color 0.2s ease-in-out',
    },
  };
});

// Character count component
const CharacterCount = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  textAlign: 'right',
}));

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({
  type = 'text',
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  autoComplete = 'off',
  showPasswordToggle = false,
  startIcon,
  endIcon,
  validation = {},
  onValidate,
  sx = {},
  textFieldSx,
  InputProps = {},
  inputProps: rawInputProps = {},
  showCharacterCount = false,
  showValidationIcon = true,
  isLoading = false,
  deferValidation = false,
  helperTop,
  helperAttached,
  helperBottom,
  showErrorBelow = true,
  label: fieldLabel,
  value,
  onChange,
  onBlur,
  inputMode,
  autoCapitalize = 'none',
  autoCorrect = 'off',
  spellCheck = false,
  // ConditionalContainer props
  title,
  description,
  containerVariant = 'plain',
  showErrorInContainer = true,
  // LogRocket privacy masking - defaults to true
  dataPrivate = true,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [hasValue, setHasValue] = useState(false);

  // Determine actual input type
  let inputType = type;
  if (type === 'password' && showPassword) {
    inputType = 'text';
  }

  // Handle password visibility toggle
  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  // Derive inputMode for mobile keyboards; default to normal text keyboard
  const derivedInputMode = inputMode ?? (
    type === 'email' ? 'email' :
      type === 'tel' ? 'tel' :
        type === 'url' ? 'url' :
          type === 'number' ? 'numeric' :
            'text'
  );

  // Validation logic
  const validateInput = (inputValue: string) => {
    let error = '';

    // Required validation
    if (validation.required && (!inputValue || inputValue.trim() === '')) {
      error = 'This field is required';
    }

    // Min/Max length validation
    if (inputValue && validation.minLength && inputValue.length < validation.minLength) {
      error = `Minimum ${validation.minLength} characters required`;
    }

    if (inputValue && validation.maxLength && inputValue.length > validation.maxLength) {
      error = `Maximum ${validation.maxLength} characters allowed`;
    }

    // Pattern validation
    if (inputValue && validation.pattern) {
      const regex = new RegExp(validation.pattern, validation.patternFlags);
      if (!regex.test(inputValue.trim())) {
        error = validation.patternMessage || 'Invalid format';
      }
    }

    // Email validation
    if (type === 'email' && inputValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(inputValue)) {
        error = 'Please enter a valid email address';
      }
    }

    // URL validation
    if (type === 'url' && inputValue) {
      try {
        new URL(inputValue);
      } catch {
        error = 'Please enter a valid URL';
      }
    }

    if (!deferValidation) {
      setValidationError(error);

      if (onValidate) {
        onValidate(error === '', error);
      }
    }

    return error === '';
  };

  // Handle input change with validation
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let inputValue = event.target.value;

    // For tel type, filter to only allow digits, plus sign, and spaces
    if (type === 'tel') {
      inputValue = inputValue.replace(/[^0-9+\s]/g, '');
      // Update the event target value with filtered value
      (event.target as HTMLInputElement).value = inputValue;
    }

    setHasValue(inputValue.length > 0);

    // Validate on change if enabled and validation rules exist
    if (!deferValidation && Object.keys(validation).length > 0) {
      validateInput(inputValue);
    }

    if (onChange) {
      onChange(event);
    }
  };

  // Handle blur for validation
  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const inputValue = event.target.value;
    if (!deferValidation) {
      validateInput(inputValue);
    }

    if (onBlur) {
      onBlur(event);
    }
  };

  const handleFocus = () => {
    // Focus handling
  };

  // Determine validation icon
  const getValidationIcon = () => {
    if (deferValidation || !showValidationIcon || !hasValue) return null;

    if (validationError) {
      return <ErrorIcon color="error" fontSize="small" />;
    }

    // Removed green checkmark - no icon for valid state
    return null;
  };

  // Build MUI InputProps (adornments etc.)
  const muiInputProps: any = {
    ...InputProps,
    startAdornment: startIcon ? (
      <InputAdornment position="start" sx={{ color: 'action.active' }}>
        {startIcon}
      </InputAdornment>
    ) : InputProps.startAdornment,

    endAdornment: (
      <>
        {/* Validation icon */}
        {getValidationIcon() && (
          <InputAdornment position="end">
            {getValidationIcon()}
          </InputAdornment>
        )}

        {/* Password toggle */}
        {(type === 'password' && showPasswordToggle) && (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handlePasswordToggle}
              edge="end"
              size="small"
              sx={{
                '&:hover': (theme) => ({
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                }),
              }}
            >
              {showPassword ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        )}

        {/* Custom end icon */}
        {endIcon && (
          <InputAdornment position="end" sx={{ color: 'action.active' }}>
            {endIcon}
          </InputAdornment>
        )}

        {InputProps.endAdornment}
      </>
    )
  };

  const handleNumberWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    // Preserve any consumer-provided wheel handler first
    const rawOnWheel = (rawInputProps as { onWheel?: unknown } | undefined)
      ?.onWheel;
    if (typeof rawOnWheel === "function") {
      rawOnWheel(event);
    }

    // On many browsers, wheel over <input type="number"> changes the value
    // and prevents the user from scrolling the page/container.
    // Blurring the input restores normal scrolling.
    event.currentTarget.blur();
  };

  // Build HTML input props (keyboard behavior)
  const htmlInputProps: any = {
    ...(rawInputProps as any),
    inputMode: derivedInputMode,
    autoCapitalize,
    autoCorrect,
    spellCheck,
    // Add maxLength to prevent typing beyond limit
    ...(validation.maxLength && { maxLength: validation.maxLength }),
    // LogRocket privacy masking - add data-private attribute for PII fields
    ...(dataPrivate && { 'data-private': true }),
    ...(inputType === "number" ? { onWheel: handleNumberWheel } : {}),
  };

  const textFieldValue = value !== undefined ? value : '';
  const characterCount = String(textFieldValue).length;

  // Always handle errors within TextField, not in container

  // Content to render inside ConditionalContainer
  const textFieldContent = (
    <Wrapper>
      {fieldLabel && !title && (
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, color: 'primary.main', fontSize: isMobile ? '14px' : '16px', lineHeight: isMobile ? '20px' : '22px' }}>
          {fieldLabel}
          {validation.required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          )}
        </Typography>
      )}
      {helperTop && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px' }}>
          {helperTop}
        </Typography>
      )}
      {/* Error message above input field */}
      {((!deferValidation && validationError) || props.error) && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            mb: 1,
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: isMobile ? '16px' : '20px',
            fontWeight: 500
          }}
        >
          {(!deferValidation && validationError) || props.helperText || 'This field is required'}
        </Typography>
      )}
      <StyledTextField
        ref={ref}
        type={inputType}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        autoComplete={autoComplete}
        margin="none"
        error={((!deferValidation && !!validationError) || (props.error ?? false))}
        helperText={(!deferValidation && validationError) ? "" : (props.helperText || "")} // Allow MUI helperText when no local validation error
        FormHelperTextProps={{ sx: { display: 'none', margin: 0, height: 0 } }} // Hide helper text completely and remove space
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        InputProps={muiInputProps}
        inputProps={htmlInputProps}
        value={value}
        disabled={props.disabled ?? false}
        className={!deferValidation && validationError ? 'ie-hub-field-error' : ''}
        sx={[
          {
            '& .MuiInputBase-input': {
              fontSize: isMobile ? '16px' : '18px',
              lineHeight: isMobile ? '22px' : '24px',
            },
            // Force red border when there's a validation error
            '&.ie-hub-field-error': {
              '& .MuiOutlinedInput-root': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D5391F !important',
                  borderWidth: '2px !important',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D5391F !important',
                  borderWidth: '2px !important',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D5391F !important',
                  borderWidth: '2px !important',
                },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D5391F !important',
                  borderWidth: '2px !important',
                },
              },
            },
            // Fallback approach for error styling
            ...(!deferValidation && !!validationError
              ? {
                  '& .MuiOutlinedInput-root': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D5391F !important',
                      borderWidth: '2px !important',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D5391F !important',
                      borderWidth: '2px !important',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D5391F !important',
                      borderWidth: '2px !important',
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D5391F !important',
                      borderWidth: '2px !important',
                    },
                  },
                }
              : {}),
          },
          textFieldSx,
        ]}
        {...props}
      />
      {helperAttached && (
        <AttachedBlock>{helperAttached}</AttachedBlock>
      )}
      {showCharacterCount && validation.maxLength && characterCount >= (validation.maxLength * 0.8) && (
        <CharacterCount
          color={characterCount >= validation.maxLength ? 'error' : 'textSecondary'}
        >
          {characterCount}/{validation.maxLength}
        </CharacterCount>
      )}
      {helperBottom && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: validationError ? 0.75 : 1.25, mb: 0, fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px' }}>
          {helperBottom}
        </Typography>
      )}
    </Wrapper>
  );

  const hasContainerError = ((!deferValidation && !!validationError) || (props.error ?? false));

  const containerProps: any = {
    ifError: false, // Never show error in container
    variant: containerVariant,
    sx: {
      ...(sx as Record<string, unknown>),
      // Add red left border when there's a validation error
      ...(hasContainerError && {
        borderLeft: '4px solid',
        borderLeftColor: 'error.main',
        paddingLeft: 1.5,
        paddingBottom: 0, // Reduce bottom padding to minimize border height
        minHeight: 'auto',
        height: 'fit-content',
        maxHeight: '90%', // or whatever height you prefer
      }),
      // Ensure consistent width regardless of error state
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch', // Changed from flex-start to stretch to maintain full width
      gap: 1,
      mb: 0.5,
      // Reduce overall container height when in error state
      ...(hasContainerError && {
        paddingTop: 0,
        marginBottom: 0,
      })
    },
  };

  if (title || (fieldLabel && title !== undefined)) {
    containerProps.title = title || fieldLabel;
  }

  if (description) {
    containerProps.description = description;
  }

  // Remove container error message logic since we handle errors in TextField
  // if (containerErrorMessage) {
  //   containerProps.errorMessage = containerErrorMessage;
  // }

  return (
    <ConditionalContainer {...containerProps}>
      {textFieldContent}
    </ConditionalContainer>
  );
});

TextField.displayName = 'TextField';

export default TextField;
